import { useState } from 'react';

import { Button } from '@components/button';
import { Checkbox } from '@components/checkbox';
import { IconFormIndicatorWarning } from '@components/icons/form-indicator-warning';
import { hideModal, Modal, useModal } from '@components/modal';
import { Select } from '@components/select';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { EmailAddressWithDomain } from 'api/company/requests';

type ModalProps = {
  mode: 'remove' | 'disable';
  availableEmails: EmailAddressWithDomain[];
  onConfirm: (fallbackShortId: string) => Promise<void>;
};

export const useFallbackEmailSelectionModal = () => useModal(FallbackEmailSelectionModal);

const FallbackEmailSelectionModal = ({ mode, availableEmails, onConfirm }: ModalProps) => {
  const { t } = useScopedTranslate(locale);
  const [selectedFallbackId, setSelectedFallbackId] = useState<string>('');
  const [confirmed, setConfirmed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const isRemove = mode === 'remove';
  const title = isRemove ? t('removeTitle') : t('disableTitle');
  const question = isRemove ? t('removeQuestion') : t('disableQuestion');
  const buttonText = isRemove ? t('removeButton') : t('disableButton');

  const emailOptions = availableEmails.map((addr) => ({
    value: addr.shortId,
    label: addr.fromAddress,
  }));

  const canSubmit = selectedFallbackId && confirmed;

  const handleConfirm = async () => {
    if (!canSubmit) return;
    setIsLoading(true);
    try {
      await onConfirm(selectedFallbackId);
      hideModal();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isLoading={isLoading}>
      <Modal.SimpleHeader title={title} icon={<IconFormIndicatorWarning color="orange100" />} />
      <Modal.Content>
        <Stack space={6}>
          <Text text={question} font="regular14" color="textSecondary" />

          <Text text={t('description')} font="regular14" color="textSecondary" />

          <Select
            value={selectedFallbackId}
            onChange={setSelectedFallbackId}
            options={emailOptions}
            placeholder={t('selectPlaceholder')}
          />

          <Separator />

          <Stack space={2}>
            <Text text={t('pleaseConfirm')} font="bold14" />
            <Checkbox value={confirmed} onChange={setConfirmed} label={t('confirmCheckbox')} />
          </Stack>
        </Stack>
      </Modal.Content>
      <Modal.Footer>
        <Stack orientation="horizontal" justify="flex-end" space={2}>
          <Button variant="secondary" onClick={hideModal} text={t('core.cancel')} />
          <Button
            variant="primary-red"
            onClick={handleConfirm}
            text={buttonText}
            disabled={!canSubmit}
          />
        </Stack>
      </Modal.Footer>
    </Modal>
  );
};

const locale = {
  en: {
    removeTitle: 'Remove email address',
    disableTitle: 'Disable email address',
    removeQuestion: 'Are you sure you want to remove the email address?',
    disableQuestion: 'Are you sure you want to disable the email address?',
    description:
      'For actions that already use this email you must replace it with another email address so that they continue to run',
    selectPlaceholder: 'Select email...',
    pleaseConfirm: 'Please confirm',
    confirmCheckbox: 'I understand',
    removeButton: 'Remove & replace',
    disableButton: 'Disable & replace',
  },
};
